import React, { useMemo, useRef, useState } from 'react';
import { Box, Stack } from '@mui/material';
import dayjs from 'dayjs';

import { Column, DataTable } from '../../../common/DataTable';
import { Pagination } from '../../../common/DataTable/Pagination';
import { FiltersButton, SearchTextField } from '../../Paylinks/components';
import { RefreshButton } from '../../Paylinks/components/RefreshButton';
import { useGetPortalUserAuditLogsQuery } from '../../../store/modules/merchant/api';
import type {
  GetPortalUserAuditLogsQuery,
  PortalUserAuditLogEntry,
} from '../../../store/modules/merchant/types';
import {
  PortalUserAuditLogFiltersPanel,
  PortalUserAuditLogQueryFilters,
} from './PortalUserAuditLogFiltersPanel';
import { PortalUserAuditActionChip } from './PortalUserAuditActionChip';
import { PortalUserAuditLogDetails } from './PortalUserAuditLogDetails';
import {
  PortalUserAuditActorCell,
  PortalUserAuditTargetUserCell,
} from './PortalUserAuditUserCells';

const auditLogColumns: Column<PortalUserAuditLogEntry>[] = [
  {
    id: 'action',
    headerName: 'Action',
    renderCell: (row) => (
      <PortalUserAuditActionChip
        action={row.action}
        emailType={row.metadata?.emailType}
        size="small"
      />
    ),
    maxWidth: 260,
    minWidth: 240,
  },
  {
    headerName: 'Date',
    id: 'timestamp_date',
    valueGetter: (row) => dayjs(row.timestamp).format('MM/DD/YY'),
    maxWidth: 100,
    minWidth: 100,
  },
  {
    headerName: 'Time',
    id: 'timestamp_time',
    valueGetter: (row) => dayjs(row.timestamp).format('hh:mm:ss A'),
    maxWidth: 120,
    minWidth: 120,
  },
  {
    headerName: 'Performed by',
    id: 'actorEmail',
    renderCell: (row) => <PortalUserAuditActorCell auditLog={row} />,
    minWidth: 240,
  },
  {
    headerName: 'Affected user',
    id: 'targetUser',
    renderCell: (row) => <PortalUserAuditTargetUserCell auditLog={row} />,
    minWidth: 300,
  },
  {
    id: 'details',
    headerName: 'Details',
    renderCell: (row) => <PortalUserAuditLogDetails auditLog={row} />,
    maxWidth: 80,
    minWidth: 80,
    align: 'center',
  },
];

export const PortalUserAuditLogsTable = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResetKey, setSearchResetKey] = useState(0);
  const [filters, setFilters] = useState<PortalUserAuditLogQueryFilters>({});
  const [isFiltersPanelOpen, setIsFiltersPanelOpen] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const tableRef = useRef<HTMLDivElement>(null);

  const queryParams = useMemo<GetPortalUserAuditLogsQuery>(
    () => ({
      pageNumber,
      pageSize,
      orderBy: 'desc',
      ...(searchQuery.trim() ? { searchQuery: searchQuery.trim() } : {}),
      ...filters,
    }),
    [pageNumber, pageSize, searchQuery, filters],
  );

  const { data: response, isFetching, refetch } = useGetPortalUserAuditLogsQuery(queryParams);

  const filtersCount = useMemo(() => {
    let count = 0;
    if (filters.action?.length || filters.emailType?.length) count += 1;
    if (filters.createdAfter) count += 1;
    if (filters.createdBefore) count += 1;
    return count;
  }, [filters]);

  const handleRefresh = () => {
    setSearchResetKey((key) => key + 1);
    refetch();
  };

  return (
    <>
      <Stack gap={2}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', lg: 'row' },
            gap: 2,
            justifyContent: 'space-between',
            alignItems: { xs: 'stretch', lg: 'center' },
          }}
        >
          <Stack direction="row" gap={2} alignItems="center">
            <RefreshButton onClick={handleRefresh} disabled={isFetching} />
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} gap={2} alignItems="center">
            <FiltersButton
              filtersCount={filtersCount}
              onClick={() => setIsFiltersPanelOpen(true)}
            />
          </Stack>
        </Box>
        <SearchTextField
          label="Search by email or name"
          defaultValue={searchQuery}
          resetKey={searchResetKey}
          onSearch={(value) => {
            setSearchQuery(value);
            setPageNumber(1);
          }}
        />
        <DataTable
          rows={response?.data ?? []}
          columns={auditLogColumns}
          getRowId={(row) => row.auditLogId}
          isLoading={isFetching}
          pageSize={pageSize}
          tableHeight="calc(100vh - 315px)"
          noRowsMessage="No user audit logs found."
          tableRef={tableRef}
          renderPagination={() => (
            <Pagination
              itemCount={response?.data.length ?? 0}
              itemTotal={response?.metadata.totalRecords ?? 0}
              pageCount={response?.metadata.totalPages ?? 0}
              onPageChange={(_, page) => {
                setPageNumber(page);
                tableRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              page={pageNumber}
              pageSize={pageSize}
              pageSizeOptions={[10, 25, 50]}
              onPageSizeChange={(newPageSize) => {
                setPageNumber(1);
                setPageSize(newPageSize);
              }}
              disabled={isFetching}
            />
          )}
        />
      </Stack>
      <PortalUserAuditLogFiltersPanel
        isOpen={isFiltersPanelOpen}
        onClose={() => setIsFiltersPanelOpen(false)}
        onFiltersApply={(nextFilters) => {
          setPageNumber(1);
          setFilters(nextFilters);
        }}
      />
    </>
  );
};
